// File paths in the crew-docs bucket - ONE rule for every upload (browser, upload-cert, AI) and every reader:
//   <area>/<vessel id>/<timestamp>_<clean name>        (todo / refit: <area>/<vessel id>/<item id>/<timestamp>_<clean name>)
// The database checks the same shape: storage write rules + can_read_file in sql/upload-paths.sql (keep AREAS in step with hc_file_area_module there).

// area -> permission module of crew logins ('*' = anyone with access to the vessel, 'apa' = APA's own rule)
export const AREAS = {
  documents: 'documents', manuals: 'manuals', ga: 'todo', todo: 'todo', refit: 'refit',
  crew_certs: 'crew', crew_lists: 'crew', crew_photos: 'crew', hours_of_rest: 'hours_rest', officer_reviews: 'officer_review',
  checklists: 'checklists', drills: 'lsa', passage_plans: 'passage', risk_assessments: 'risk', bunkering: 'logbooks',
  purchase_orders: 'purchase_orders', announcements: 'announcements', apa: 'apa', ism_audits: 'ism_audits', photos: '*',
  sop_media: 'manuals',   // sop-visual: an SOP's step photos / video / cover: sop_media/<vessel id>/<SOP id>/<timestamp>_<name>
}
export const SUB_AREAS = ['todo', 'refit', 'sop_media']   // may have one <item id> folder (sop_media: the SOP's manual id)
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const UUID_RE = new RegExp('^' + UUID + '$')
export const isUuid = v => UUID_RE.test(String(v || ''))

// any user-given file name -> [A-Za-z0-9_.-], at most 100 characters, never "..", never starting with a dot
export function cleanFileName(name, max = 100) {
  let s = String(name == null ? '' : name).normalize('NFKD').replace(/[̀-ͯ]/g, '')
  s = s.replace(/[^A-Za-z0-9_.-]+/g, '_').replace(/\.{2,}/g, '_').replace(/^[._-]+/, '')
  if (s.length > max) { const m = s.match(/\.[A-Za-z0-9]{1,6}$/); const ext = m ? m[0] : ''; s = s.slice(0, max - ext.length).replace(/[._-]+$/, '') + ext }
  return s || 'file'
}

// the storage path for a new file; throws on an unknown area / vessel / item id
export function filePath(area, vesselId, name, opts = {}) {
  if (!AREAS[area]) throw new Error('Unknown upload area: ' + area)
  if (!isUuid(vesselId)) throw new Error('No vessel for this upload')
  const sub = opts.sub != null && opts.sub !== '' ? String(opts.sub) : null
  if (sub && (!SUB_AREAS.includes(area) || !isUuid(sub))) throw new Error('Sub folder not allowed here')
  const ts = opts.now != null ? opts.now : Date.now()
  return area + '/' + vesselId + '/' + (sub ? sub + '/' : '') + ts + '_' + cleanFileName(name)
}

// new-layout path -> { area, vesselId, sub, name }; null for anything else (old layout, "..", empty parts, backslash, unknown area)
const NEW_RE = new RegExp('^([a-z_]+)/(' + UUID + ')/(?:(' + UUID + ')/)?([^/\\\\]+)$')
export function parsePath(path) {
  const p = String(path || '')
  if (!p || p.includes('..') || p.includes('//') || p.includes('\\') || /[\u0000-\u001f]/.test(p)) return null
  const m = p.match(NEW_RE)
  if (!m || !AREAS[m[1]] || (m[3] && !SUB_AREAS.includes(m[1]))) return null
  return { area: m[1], vesselId: m[2], sub: m[3] || null, name: m[4] }
}

// a stored link -> its path in the bucket (decoded), or null when it is not a crew-docs file link
const URL_RE = /^https:\/\/[^/?#]+\/storage\/v1\/object\/(?:public|sign|authenticated)\/crew-docs\/([^?#]+)(?:[?#].*)?$/
export function pathOfUrl(url) {
  const m = String(url || '').match(URL_RE)
  if (!m) return null
  try { return decodeURIComponent(m[1]) } catch (e) { return null }
}
// a link or path the browser sends (chat photo, receipt): only a file of THIS vessel's own folder of one of the given areas
export function ownFileUrl(url, vesselId, areas) {
  const p = parsePath(storedPath(url))
  return !!p && p.vesselId === String(vesselId) && (!areas || areas.includes(p.area))
}
// old upload-cert "crewName" values -> area (anything else was a crew member's name = a crew certificate)
export function areaFromLegacy(crewName) {
  const c = String(crewName || '')
  return { photos: 'photos', bunkering: 'bunkering', purchase_orders: 'purchase_orders', apa: 'apa' }[c] || (AREAS[c] ? c : 'crew_certs')
}

// What a record stores for a file: the bucket PATH (new uploads, e.g. "documents/<vessel id>/1760000000000_x.pdf"), or a full
// public link (rows saved before fixes-1). Every reader goes through storedPath; files are only ever opened through a short-lived
// signed link (src/lib/fileAccess.js -> /api/upload-cert action 'sign' -> can_read_file).
// a stored value -> its path in crew-docs, or null when it is no crew-docs file (bare paths: the new layout only)
export function storedPath(v) {
  const s = String(v == null ? '' : v).trim()
  if (!s) return null
  if (/^https?:\/\//i.test(s)) return pathOfUrl(s)
  return parsePath(s) ? s : null
}
export const isStoredFile = v => storedPath(v) != null
// What may go on to the permission check in /api/upload-cert 'sign'. The browser sends the PATH of a file (storedPath), and an old file's
// path is not in the new layout, so storedPath refuses it: every old file was answered "denied" without being checked. Here any
// well-formed crew-docs path passes, and can_read_file alone decides (old layout: only through storage_file_owner).
// A link is judged AFTER decoding, so "%2e%2e", "%2F", "%5C", "%00" count as what they are, and what still looks encoded ("%252e") is refused.
const badSignPath = p => p.length > 1024 || p.startsWith('/') || p.endsWith('/') || p.includes('//') || p.includes('\\') || p.includes('?') || p.includes('#')
  || /[\u0000-\u001f\u007f]/.test(p) || /%[0-9a-f]{2}/i.test(p) || p.split('/').some(x => x === '.' || x === '..') || p.includes('..')
export function signablePath(v) {
  if (typeof v !== 'string') return null
  const s = v.trim()
  if (!s) return null
  const p = /^https?:\/\//i.test(s) ? pathOfUrl(s) : s
  return p == null || badSignPath(p) ? null : p
}
// a stored value -> a full link (the old public form; opening it still goes through signing) for text the AI / exports show
export function fileLink(v, base) {
  const s = String(v == null ? '' : v).trim()
  if (/^https?:\/\//i.test(s)) return s
  const p = storedPath(s), b = String(base || '').replace(/\/$/, '')
  return p && b ? b + '/storage/v1/object/public/crew-docs/' + p.split('/').map(encodeURIComponent).join('/') : s
}
